import moment from "moment";

export const CLINIC_TIMEZONES = Object.freeze([
  { value: "Asia/Jakarta", label: "WIB — Jakarta (UTC+7)", offset: "+07:00", minutes: 420 },
  { value: "Asia/Makassar", label: "WITA — Makassar (UTC+8)", offset: "+08:00", minutes: 480 },
  { value: "Asia/Jayapura", label: "WIT — Jayapura (UTC+9)", offset: "+09:00", minutes: 540 },
]);

const DEFAULT_TIMEZONE = "Asia/Jakarta";

function readJson(key) {
  try {
    return JSON.parse(window.localStorage.getItem(key)) || {};
  } catch (error) {
    return {};
  }
}

export function clinicTimezone() {
  const configured = readJson("configuration").timezone;
  return CLINIC_TIMEZONES.some(item => item.value === configured)
    ? configured
    : DEFAULT_TIMEZONE;
}

export function clinicTimezoneInfo() {
  return CLINIC_TIMEZONES.find(item => item.value === clinicTimezone()) || CLINIC_TIMEZONES[0];
}

// API timestamps are historically stored as tenant-local wall-clock values
// without an offset. Attach the clinic offset before Intl formatting so a
// user's laptop timezone can never shift a transaction into another hour/day.
function normalizedDate(value) {
  if (value instanceof Date || typeof value === "number") return new Date(value);
  if (!value) return null;

  const text = String(value).trim();
  const naive = text.match(/^(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2})(?::(\d{2}))?(?:\.\d+)?)?$/);
  if (naive) {
    const time = naive[2] ? `${naive[2]}:${naive[3] || "00"}` : "00:00:00";
    return new Date(`${naive[1]}T${time}${clinicTimezoneInfo().offset}`);
  }

  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatClinicDateTime(value, options = {}) {
  const date = normalizedDate(value);
  if (!date) return "-";
  const usesStyle = options.dateStyle || options.timeStyle;
  const formatOptions = usesStyle
    ? { timeZone: clinicTimezone(), hourCycle: "h23", ...options }
    : {
        timeZone: clinicTimezone(),
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
        ...options,
      };
  return new Intl.DateTimeFormat("id-ID", formatOptions).format(date);
}

export function formatClinicDate(value, options = {}) {
  return formatClinicDateTime(value, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: undefined,
    minute: undefined,
    ...options,
  });
}

export function formatClinicTime(value = new Date(), options = {}) {
  return formatClinicDateTime(value, {
    day: undefined,
    month: undefined,
    year: undefined,
    hour: "2-digit",
    minute: "2-digit",
    ...options,
  });
}

export function clinicNowInput(includeTime = true) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: clinicTimezone(),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date()).reduce((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});
  const date = `${parts.year}-${parts.month}-${parts.day}`;
  return includeTime ? `${date}T${parts.hour}:${parts.minute}` : date;
}

export function applyClinicTimezone() {
  const info = clinicTimezoneInfo();
  window.medhopTimezone = info.value;
  window.medhopTimezoneLabel = info.label.split(" ")[0];
  window.medhopToday = () => clinicNowInput(false);

  // Existing report filters use moment(). They must generate the clinic's
  // current wall time even when opened from a device in another timezone.
  moment.now = () => {
    const actual = Date.now();
    const browserOffset = -new Date(actual).getTimezoneOffset();
    return actual + (info.minutes - browserOffset) * 60000;
  };

  return info.value;
}

export default {
  CLINIC_TIMEZONES,
  clinicTimezone,
  clinicTimezoneInfo,
  formatClinicDateTime,
  formatClinicDate,
  formatClinicTime,
  clinicNowInput,
  applyClinicTimezone,
};
